import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Asset } from '@/types/models/assets.types';
import { FiltersValues, FilterData } from '@/components/uiKit/FiltersGroup/types';
import { DEFAULT_DATE_PICKER_DATES } from '../constants';

interface assetsPreviewState {
  selectedView: 'grid' | 'list' | 'table';
  allFiltersValues?: FiltersValues;
  initialFiltersValues?: FiltersValues;
  currentFiltersValues?: FiltersValues;
  currentFiltersData?: FilterData[];
  selectedAssets?: Asset[];
  dateRange: { from: string; to: string };
  filtersByAssetType: { [assetType: string]: FiltersValues };
  searchByAssetType: { [assetType: string]: string };
  // Faults-specific filter state (separate from overview filters)
  faultsAllFiltersValues?: FiltersValues;
  faultsInitialFiltersValues?: FiltersValues;
  faultsCurrentFiltersValues?: FiltersValues;
  faultsCurrentFiltersData?: FilterData[];
}

const initialState: assetsPreviewState = {
  selectedView: 'grid',
  dateRange: {
    ...DEFAULT_DATE_PICKER_DATES,
  },
  filtersByAssetType: {},
  searchByAssetType: {},
};

const assetsPreviewSlice = createSlice({
  initialState,
  name: 'assetsPreview',
  reducers: {
    setSelectedView(state, action) {
      state.selectedView = action.payload;
    },
    setCurrentFiltersData(state, action) {
      state.currentFiltersData = action.payload;
    },
    setCurrentFiltersValues(state, action) {
      state.currentFiltersValues = action.payload;
    },
    setAllFiltersValues(state, action) {
      state.allFiltersValues = action.payload;
    },
    setInitialFiltersValues(state, action) {
      state.initialFiltersValues = action.payload;
    },
    setSelectedAssets(state, action) {
      state.selectedAssets = { ...action.payload };
    },
    setDateRangePickerDate: (state, action) => {
      state.dateRange = action.payload;
    },
    resetDateRangePicker: (state) => {
      state.dateRange = DEFAULT_DATE_PICKER_DATES;
    },
    setFiltersByAssetType: (state, action: PayloadAction<{ assetType: string; filters: FiltersValues }>) => {
      state.filtersByAssetType[action.payload.assetType] = action.payload.filters;
    },
    setSearchByAssetType: (state, action: PayloadAction<{ assetType: string; search: string }>) => {
      state.searchByAssetType[action.payload.assetType] = action.payload.search;
    },
    resetAllFilters: (state) => {
      state.filtersByAssetType = {};
    },
    resetAllSearch: (state) => {
      state.searchByAssetType = {};
    },
    // Faults-specific filter actions
    setFaultsCurrentFiltersData(state, action) {
      state.faultsCurrentFiltersData = action.payload;
    },
    setFaultsCurrentFiltersValues(state, action) {
      state.faultsCurrentFiltersValues = action.payload;
    },
    setFaultsAllFiltersValues(state, action) {
      state.faultsAllFiltersValues = action.payload;
    },
    setFaultsInitialFiltersValues(state, action) {
      state.faultsInitialFiltersValues = action.payload;
    },
    resetFaultsFilters: (state) => {
      state.faultsAllFiltersValues = undefined;
      state.faultsInitialFiltersValues = undefined;
      state.faultsCurrentFiltersValues = undefined;
      state.faultsCurrentFiltersData = undefined;
    },
  },
});

export const AssetsPreviewSliceReducer = assetsPreviewSlice.reducer;
export const {
  setSelectedView,
  setCurrentFiltersData,
  setCurrentFiltersValues,
  setAllFiltersValues,
  setInitialFiltersValues,
  setSelectedAssets,
  setDateRangePickerDate,
  resetDateRangePicker,
  setFiltersByAssetType,
  setSearchByAssetType,
  resetAllFilters,
  resetAllSearch,
  // Faults-specific filter actions
  setFaultsCurrentFiltersData,
  setFaultsCurrentFiltersValues,
  setFaultsAllFiltersValues,
  setFaultsInitialFiltersValues,
  resetFaultsFilters,
} = assetsPreviewSlice.actions;
